/**
 * EmergencyFund — balance, target, progress bar, months of coverage.
 * Available to ALL tiers (basic version).
 */
import { NumberField, ProgressBar, Money, StatCell } from "./primitives";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { useState } from "react";
import { Button } from "@/components/ui/button";

export default function EmergencyFund() {
  const { plan, summary, patch } = useFinancialPlan();
  const ef = plan?.emergency_fund || { balance: 0, goal_months: 6 };
  const [balance, setBalance] = useState(ef.balance ?? 0);
  const [months, setMonths] = useState(ef.goal_months ?? 6);
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    await patch({ emergency_fund: { balance, goal_months: months, monthly_expenses_snapshot: null } });
    setDirty(false);
  };

  const s = summary || {};
  const tone = s.emergency_fund_progress_pct < 50 ? "warn" : s.emergency_fund_progress_pct < 100 ? "default" : "good";

  return (
    <div className="space-y-4" data-testid="emergency-fund-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatCell label="Current balance" value={<Money amount={s.emergency_fund_balance} currency={plan?.currency} />} sub={`${s.emergency_fund_months_covered} mo covered`} testId="ef-current" tone={tone} />
        <StatCell label="Recommended" value={<Money amount={s.emergency_fund_recommended} currency={plan?.currency} />} sub={`${s.emergency_fund_goal_months} mo of expenses`} testId="ef-recommended" />
        <StatCell label="Progress" value={`${s.emergency_fund_progress_pct}%`} sub="to full coverage" testId="ef-progress-stat" tone={tone} />
      </div>

      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Progress to goal</div>
        <ProgressBar value={s.emergency_fund_progress_pct} tone={tone === "warn" ? "warn" : tone === "good" ? "good" : "primary"} testId="ef-progress-bar" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <NumberField label="Emergency fund balance" value={balance} onChange={(v) => { setBalance(v); setDirty(true); }} prefix={plan?.currency} testId="ef-balance-input" />
        <NumberField label="Goal (months of expenses)" value={months} onChange={(v) => { setMonths(v); setDirty(true); }} suffix="mo" testId="ef-months-input" step="1" />
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={!dirty} data-testid="ef-save-btn">Save</Button>
      </div>
    </div>
  );
}
